import { ExternalLinkIcon } from 'lucide-react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import useSpeechModels from '@/modules/developerTools/hooks/useSpeechModels';
import { formatLanguageLabel } from '@/util/languageOptions';

import { rimeLanguages } from './helpers';

/** The TTS providers that only a speech strategy takes; the voice catalog lists no voices for them. */
export const CATALOG_TTS_PROVIDERS = ['deepgram', 'rime', 'google'] as const;

type CatalogTtsProvider = (typeof CATALOG_TTS_PROVIDERS)[number];

const MODEL_NAMES: Record<string, string> = {
  'aura-2': 'Aura-2',
  coda: 'Coda',
  mistv3: 'Mist v3',
  'chirp3-hd': 'Chirp 3: HD',
};

const VOICE_LIBRARY: Record<CatalogTtsProvider, { url: string; example: string }> = {
  deepgram: { url: 'https://developers.deepgram.com/docs/tts-models', example: 'thalia-en' },
  rime: { url: 'https://docs.rime.ai/api-reference/voices', example: 'luna' },
  google: {
    url: 'https://cloud.google.com/text-to-speech/docs/chirp3-hd',
    example: 'en-US-Chirp3-HD-Charon',
  },
};

interface CatalogVoiceCardProps {
  provider: CatalogTtsProvider;
  /** Form path of the step's config. */
  basePath: string;
}

/**
 * Deepgram, Rime and Google TTS: the model, the voice ID from the provider's voice library, and
 * Rime's language. The fields follow orchestrator's `SPEECH_PROVIDER_CONFIG` blocks.
 */
export const CatalogVoiceCard = ({ provider, basePath }: CatalogVoiceCardProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const { providers: catalog } = useSpeechModels();
  const modelId = useWatch({ control, name: `${basePath}.model_id` }) as string | undefined;
  const languageCode = useWatch({ control, name: `${basePath}.language_code` }) as
    string | undefined;

  const models = catalog.find((entry) => entry.provider === provider)?.tts ?? [];
  const languages = rimeLanguages(modelId);
  const library = VOICE_LIBRARY[provider];

  const selectModel = (next: string) => {
    setValue(`${basePath}.model_id`, next, { shouldDirty: true });
    if (provider === 'rime' && languageCode && !rimeLanguages(next).includes(languageCode)) {
      setValue(`${basePath}.language_code`, undefined, { shouldDirty: true });
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <FormField
        control={control}
        name={`${basePath}.model_id`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.model')}</FormLabel>
            <Select value={field.value ?? ''} onValueChange={selectModel}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {models.map((model) => (
                  <SelectItem key={model} value={model}>
                    {MODEL_NAMES[model] ?? model}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name={`${basePath}.voice_id`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('integrations.voice_common.voice.voice_id')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? ''}
                placeholder={library.example}
                autoComplete="off"
              />
            </FormControl>
            <FormDescription>
              {t('ai_agents.speech_strategy.voice_id_help', { 0: library.example })}{' '}
              <a
                href={library.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-foreground underline underline-offset-4"
              >
                {t('ai_agents.speech_strategy.voice_library')}
                <ExternalLinkIcon className="size-3" />
              </a>
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {provider === 'rime' && (
        <FormField
          control={control}
          name={`${basePath}.language_code`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('ai_agents.speech_strategy.voice_language')}</FormLabel>
              <Select value={field.value ?? ''} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('ai_agents.speech_strategy.voice_language_any')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {languages.map((code) => (
                    <SelectItem key={code} value={code}>
                      {formatLanguageLabel(code)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                {t('ai_agents.speech_strategy.voice_language_help')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
